import { HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { API_PATHS } from 'app/core/api-clients/api-paths';
import { IResponseModel } from 'app/core/models/general.model';
import { NERA_SUPPRESS_ERROR_TOAST } from 'app/core/tokens';
import { RestApiService } from 'nera-common/angular';
import { unwrapData } from 'app/shared/utils/unwrap-data';
import { map, Observable } from 'rxjs';
import {
    ConsentPurpose,
    IConsentPolicyResponse,
    IOtpChallengeResponse,
    ISubjectConsentResponse,
    IUpdateSubjectConsentRequest,
} from '../models/subject-consent.model';

const silentContext = () =>
    new HttpContext().set(NERA_SUPPRESS_ERROR_TOAST, true);

/**
 * WS-H subject-scoped consent API (design `ws-h-consent-design.md` §6, PFL-2569 MR9).
 * Endpoints under `/api/v1/me/consents` and `/api/v1/orgs/{orgId}/consents`.
 */
@Injectable({ providedIn: 'root' })
export class SubjectConsentsService extends RestApiService {
    /** `GET /api/v1/me/consents/policy` — the consent policy version every grant must carry.
     *  `background` marks a read the user did not ask for (the login gate): a failure is
     *  handled by the caller and must not raise the global error toast (kb#5786/#5788). */
    getConsentPolicyVersion(background = false): Observable<string | null> {
        return this.get<IConsentPolicyResponse>(
            API_PATHS.photoqa.consentPolicy,
            background ? { context: silentContext() } : undefined
        ).pipe(map((res) => res.data?.policyVersion ?? null));
    }

    /** `GET /api/v1/me/consents` — the caller's own per-purpose grant state, one row per
     *  {@link ConsentPurpose} (defaults to not-granted for purposes never acted on).
     *  `background`: see {@link getConsentPolicyVersion}. */
    getMyConsents(background = false): Observable<ISubjectConsentResponse[]> {
        return this.get<ISubjectConsentResponse[]>(
            API_PATHS.photoqa.myConsents,
            background ? { context: silentContext() } : undefined
        ).pipe(map((res) => res.data ?? []));
    }

    /** `GET /api/v1/orgs/{orgId}/consents` — the studio's per-purpose grant state. */
    getOrgConsents(orgId: string): Observable<ISubjectConsentResponse[]> {
        return this.get<ISubjectConsentResponse[]>(
            API_PATHS.photoqa.orgConsents(orgId)
        ).pipe(map((res) => res.data ?? []));
    }

    /** `PUT /api/v1/me/consents/{purpose}` — any authenticated user, own subject, no OTP. */
    updateMyConsent(
        purpose: ConsentPurpose,
        request: IUpdateSubjectConsentRequest
    ): Observable<ISubjectConsentResponse> {
        return this.update<IResponseModel<ISubjectConsentResponse>>(
            API_PATHS.photoqa.myConsent(purpose),
            request
        ).pipe(map((res) => unwrapData(res) as ISubjectConsentResponse));
    }

    /**
     * `PUT /api/v1/orgs/{orgId}/consents/{purpose}` — studio OWNER (or SUPER_ADMIN) only.
     * Granting `FaceAnalytics`/`ModelTraining` requires `otpChallengeId`/`otpCode` from a prior
     * {@link requestOrgConsentOtp} call (design §6 bullet 2, Q44).
     */
    updateOrgConsent(
        orgId: string,
        purpose: ConsentPurpose,
        request: IUpdateSubjectConsentRequest
    ): Observable<ISubjectConsentResponse> {
        return this.update<IResponseModel<ISubjectConsentResponse>>(
            API_PATHS.photoqa.orgConsent(orgId, purpose),
            request
        ).pipe(map((res) => unwrapData(res) as ISubjectConsentResponse));
    }

    /**
     * `POST /api/v1/orgs/{orgId}/consents/{purpose}/otp-challenge` — step 1 of the owner-signed
     * studio consent flow (WS-H MR4, design §6.2/§13.2). Emails an OTP code to the caller.
     */
    requestOrgConsentOtp(
        orgId: string,
        purpose: ConsentPurpose
    ): Observable<IOtpChallengeResponse> {
        return this.post<IResponseModel<IOtpChallengeResponse>>(
            API_PATHS.photoqa.orgConsentOtpChallenge(orgId, purpose),
            {}
        ).pipe(map((res) => unwrapData(res) as IOtpChallengeResponse));
    }

    /**
     * `PUT /api/v1/me/consents/acceptance` — the first-login Terms+AiProcessing
     * gate (WS-H §6.5/§9 MR8, PFL-2569; kanban #3306). Records BOTH purposes under
     * the one server policy version in a single call; `400` means the version is
     * stale — refetch {@link getConsentPolicyVersion} and prompt again.
     */
    recordSignupAcceptance(policyVersion: string): Observable<void> {
        return this.update<void>(API_PATHS.photoqa.consentAcceptance, {
            termsPolicyVersion: policyVersion,
            aiProcessingPolicyVersion: policyVersion,
        });
    }
}
